import {Injectable} from "@angular/core";
import {IEnterpriseWhiteLabelUpdate, IGroup, IPrivileges, IWhiteLabel} from "../app/interfaces/enterprise-interface";
import {List} from "immutable";
import {AccountVersion, EnumPricingMethod, IAccountReplyV2, IAppStatic, IDecryptCredentials, IPreferencesBusiness, IPreferencesServer, IUser} from "../app/interfaces";
import {compareIds, isNullOrUndefOrEmpty, NONE_VALUE, popupWindow} from "../app/common";
import {Memoizee} from "../app/decorators/memoizee-decorator";
import {IBlockLabelProp} from "../app/containers/editor/scene-blocks/block-constants";
import {Router} from "@angular/router";
import * as xml2js from 'xml2js';
import * as _ from 'lodash';
import moment from 'moment';
import { RC4 } from "./rc4";

export enum EnumDebugLevel {
    OFF,
    SOCIAL_SHOW_MESSAGES
}

const DEBUG_LEVEL: EnumDebugLevel = EnumDebugLevel.SOCIAL_SHOW_MESSAGES;

// export var simpleRegExp = '[\\[\\]\\-A-Za-z0-9_~=!:@\.|\ ]{3,50}';
// (https?:\/\/(?:www\.|(?!www))[^\s\.]+\.[^\s]{2,}|www\.[^\s]+\.[^\s]{2,})
// export var urlRegExp = `(https?:\/\/(?:www\.|(?!www))\.*)`

const rc4Key = '226a3a42f34ddd778ed2c3ba56644315';
const inDevModeGulpValue = false;

@Injectable()
export class Lib {

    static rc4 = new RC4(rc4Key);

    static ProcessHourStartEnd(value: string, key: string): any {
        if (_.isUndefined(!value))
            return '';
        if (key == 'hourStart')
            return `${value}:00`;
        return `${value}:59`;
    }

    /*
    mapJs: the data to work on
    index: select a table index as in boards,board_templates, board_template_viewers...
    position: first = the key as in 'global_settings' and last = value as in '7'
     */
    static MapOfIndex(mapJs, index: number, position: "first" | "last") {
        const mapJsPairs = _.toPairs(mapJs);
        const offset = position === 'first' ? 0 : 1;
        if (mapJsPairs[index] === undefined)
            return "0";
        return mapJsPairs[index][offset];
    }

    static async wait(i_time) {
        return new Promise((resolve, reject) => {
            try {
                setTimeout(() => {
                    resolve(i_time);
                }, i_time);

            } catch (e) {
                return reject('timeoutSync ' + e);
            }
        });
    }

    static EncryptUserPass(i_user, i_pass) {
        const crumb = i_user + ':SignageStudioLite:' + i_pass + ':' + ' USER';
        return Lib.rc4.doEncrypt(crumb);
    }

    static EncryptValue(i_val) {
        return Lib.rc4.doEncrypt(i_val);
    }

    static DecryptValue(i_val) {
        return Lib.rc4.doDecrypt(i_val);
    }

    static DecryptUserPass(crumb): IDecryptCredentials {
        const credentials1 = Lib.rc4.doDecrypt(crumb);
        const credentials2 = credentials1.split(':');
        return {
            user: credentials2[0],
            pass: credentials2[2],
            studio: credentials2[1],
            userType: credentials2[3]
        };
    }

    static RC4Encrypt(i_value) {
        i_value = String(i_value);
        return Lib.rc4.doEncrypt(i_value);
    }

    static RC4Decrypt(i_value) {
        return Lib.rc4.doDecrypt(i_value);
    }

    static PreferencesProcess(i_preferences: IPreferencesBusiness): IPreferencesBusiness {
        try {
            const preferences = {};
            Object.keys(i_preferences).forEach((i_key) => {
                if (i_preferences[i_key].change_type === 3 || i_key == '-1') return;
                const config = i_preferences[i_key] as IPreferencesServer;
                const jData = JSON.parse(config.preference_value);
                preferences[config.preference_key] = {id: config.preference_id, value: jData.value};
            });
            return preferences;
        } catch (e) {
            console.error('was not able to parse JSON preference_value');
            return null;
        }
    }

    static ConvertEncode(value, type: 'ENCODE' | 'COMPATIBLE') {
        switch (type) {
            case "ENCODE": {
                return value.replace(/&quot;/g, '"')  // convert quote back before converting amp
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;');
            }
            case "COMPATIBLE": {
                return value.replace(/&quot;/g, `\``)  // convert quote back before converting amp
                    .replace(/&/g, 'and')
                    .replace(/</g, '{')
                    .replace(/>/g, '}')
                    .replace(/"/g, `\``)
                    .replace(/'/g, `\``);
            }
        }
    }

    /* base64 wrap for free text stored inside XML attributes (white label login text):
       keeps ANY user input (quotes, apostrophes, html tags, hebrew/unicode) from ever
       corrupting the custom_studio XML; both directions are total - bad/empty input
       returns '' so a missing or malformed attribute can never break login */
    static EncodeTextBase64(i_text: string): string {
        if (!i_text) return '';
        try {
            return btoa(unescape(encodeURIComponent(i_text)));
        } catch (e) {
            return '';
        }
    }

    static DecodeTextBase64(i_base64: string): string {
        if (!i_base64) return '';
        try {
            return decodeURIComponent(escape(atob(i_base64)));
        } catch (e) {
            return '';
        }
    }

    static WhiteLabelXmlTemplate(action: IEnterpriseWhiteLabelUpdate, toJson, callBack?) {
        // Object.keys(action).forEach((k, v) => {
        //  action[k] = this.ConvertEncode(action[k], 'COMPATIBLE');
        // });
        let template = `
                <Studio>
                  <Application>
                    <Logo tooltip="${action.logoTooltip}"
                     link="${action.logoLink}" filename="${action.logoFilename ? action.logoFilename : 'Logo.jpg'}"/>
                    <Links home="${action.linksHome}" download="${action.linksDownload}" contact="${action.linksContact}"/>
                    <CreateAccount show="${action.createAccountOption}"/>
                    <LoginInfo html64="${Lib.EncodeTextBase64(action.loginText)}"/>
                  </Application>
                  <MainMenu>
                    <CommandGroup id="help" label="Help" icon="helpIcon">
                      <Command id="help0" label="${action.mainMenuLabel0}" href="${action.mainMenuLink0}"/>
                      <Command id="help1" label="${action.mainMenuLabel1}" href="${action.mainMenuLink1}"/>
                      <Command id="help2" label="${action.mainMenuLabel2}" href="${action.mainMenuLink2}"/>
                      <Command id="help3" label="${action.mainMenuLabel3}" href="${action.mainMenuLink3}"/>
                      <Command id="help4" label="${action.mainMenuLabel4}" href="${action.mainMenuLink3}"/>
                    </CommandGroup>
                  </MainMenu>
                  <Banner embeddedReference="${action.bannerEmbedReference}"/>
                  <Twitter show="${action.twitterShow}" link="${action.twitterLink}"/>
                  <Chat show="${action.chatShow}" link="${action.chatLink}"/>
                </Studio>`;
        if (toJson) {
            xml2js.parseString(template, (err, result) => {
                if (err) return console.log('problem parsing whitelabel XML');
                const finalResult: IWhiteLabel = {
                    $: {enabled: action.whitelabelEnabled},
                    companyName: action.companyName,
                    Studio: [result.Studio]
                };
                callBack(err, finalResult);
            });
        } else {
            template = template.replace(/>\s*/g, '>').replace(/\s*</g, '<').replace(/(\r\n|\n|\r)/gm, "");
            return template;
        }
    }

    static ParseToFloatDouble(i_value: any): number {
        return parseFloat(parseFloat(i_value).toFixed(2));
    }

    static CleanCharForXml(value: any): any {
        // tslint:disable-next-line:no-shadowed-variable
        const clean = (value: string) => {
            if (_.isUndefined(value))
                return '';
            if (_.isNull(value))
                return '';
            if (_.isNumber(value))
                return value;
            if (_.isBoolean(value))
                return value;
            value = value.replace(/\}/g, ' ');
            value = value.replace(/%/g, ' ');
            value = value.replace(/{/g, ' ');
            value = value.replace(/"/g, '`');
            value = value.replace(/'/g, '`');
            value = value.replace(/&/g, 'and');
            value = value.replace(/>/g, ' ');
            value = value.replace(/</g, ' ');
            value = value.replace(/\[/g, ' ');
            value = value.replace(/]/g, ' ');
            value = value.replace(/#/g, ' ');
            value = value.replace(/\$/g, ' ');
            value = value.replace(/\^/g, ' ');
            value = value.replace(/;/g, ' ');
            return value;
        };
        if (_.isUndefined(value))
            return '';
        if (_.isNull(value))
            return '';
        if (_.isNumber(value))
            return value;
        if (_.isBoolean(value))
            return value;
        if (_.isString(value))
            return clean(value);
        _.forEach(value, (v, k) => {
            // currently we don't support / clean arrays
            if (_.isArray(value[k]))
                return value[k] = v;
            value[k] = clean(v);
        });
        return value;
    }

    static ProcessDateField(dateString: string, addDay: boolean = false): any {
        if (_.isUndefined(dateString))
            return '';
        const epoc = dateString.match(/Date\((.*)\)/);
        if (epoc[1]) {
            const date = epoc[1].split('+')[0];
            const time = epoc[1].split('+')[1];
            let result;
            // adding +1 on save to server hack, need to ask Alon
            if (addDay) {
                result = moment(Number(date)).add(1, 'day');
            } else {
                result = moment(Number(date));
            }
            return moment(result).format('YYYY-MM-DD');
            /** moment examples
             var a = moment().unix().format()
             console.log(moment.now());
             console.log(moment().format('dddd'));
             console.log(moment().startOf('day').fromNow());
             **/
        }
    }

    /**
     * CheckFoundIndex checks if a return value is -1 and error out if in dev mode (list.findIndex or indexOf for example)
     * @param i_value
     * @param i_message
     * @returns {number}
     * @constructor
     */
    static CheckFoundIndex(i_value: number, i_message: string = 'CheckFoundIndex did not find index'): number {
        if (i_value === -1) {
            if (Lib.DevMode()) {
                throw Error(i_message);
            }
        }
        return i_value;
    }

    static AlertOnLeaveOff() {
        window.onbeforeunload = null;
    }

    /**
     *
     * @param dateString format of date + time: /Date(1469923200000+0000)/"
     * @returns {any}
     * @constructor
     */
    static ProcessDateFieldToUnix(dateString: string, addDay: boolean = false): any {
        if (_.isUndefined(dateString))
            return '';
        // adding +1 on save to server hack, need to ask Alon
        if (addDay) {
            return moment(dateString, 'YYYY-MM-DD').add(0, 'day').valueOf();
        } else {
            return moment(dateString, 'YYYY-MM-DD').valueOf();
        }
    }

    static PrivilegesXmlTemplate(defaultValues: boolean, selPrivilegeId: string, i_privileges: IPrivileges[] = null, callBack: (err, result: any | { Privilege: { Groups: Array<IGroup> } }) => any): any {
        const getAttributeGroup = (tableName: string, attribute: string): number => {
            if (_.isNull(i_privileges) || defaultValues) return 1;
            let result: any = undefined;
            i_privileges.forEach((i_privelegesModel, counter) => {
                if (i_privelegesModel.$.id === selPrivilegeId) {
                    i_privelegesModel.Groups[0].Group.forEach((group, c) => {
                        if (group.$.name === tableName)
                            result = group.$[attribute];
                    });
                }
            });
            // Group (or a newly-added attribute on it) ABSENT from the stored XML — e.g. a
            // SignStudio module a migrated legacy privilege never had — defaults to enabled (1),
            // matching the runtime fail-open in access-service (missing module -> full access).
            // A group PRESENT with visible="0" is honored (result === "0", not undefined), so this
            // never rewrites existing groups: only truly-absent ones get the default. Keeps a
            // migrated account's new SignStudio privileges ON instead of baking them OFF on save.
            if (result === undefined || result === null) return 1;
            return result;
        };
        const getPrivilegesTable = (tableName: string, attribute: string): number => {
            if (_.isNull(i_privileges) || defaultValues) return 7;
            let result: any = undefined;
            // var reseller = appStore.getState().reseller;
            // const privileges = i_privileges;//reseller.getIn(['privileges']);
            i_privileges.forEach((i_privelegesModel: IPrivileges, counter) => {
                if (i_privelegesModel.$.id === selPrivilegeId) {
                    i_privelegesModel.Groups[0].Group.forEach((group, c) => {
                        // if (group.get('tableName') === tableName)
                        //     return result = group.getIn(['columns', attribute]);
                        if (group.$.name === tableName)
                            result = group.Tables?.[0]?.$?.[attribute];
                    });
                }
            });
            // Table's group (or a newly-added table column on it) ABSENT -> default to the full
            // mask (7 = Update+Add+Delete), same rationale as getAttributeGroup. A present group
            // keeps its stored mask, including "0". Existing groups are never rewritten.
            if (result === undefined || result === null) return 7;
            return result;
        };
        /** Opt-in-safe attribute reader. Unlike getAttributeGroup (defaults newly-added
         *  attributes to '1' / visible / enabled), this defaults to '0' (off). Use for
         *  features that must NEVER auto-enable — neither on brand-new privileges nor on
         *  existing customer XMLs that haven't yet been re-saved with the new attribute.
         *  Returns the literal '0' or '1' for clean XML emission.
         *  See src/app/containers/enterprise/docs/08-station-filter-by-scene.md */
        const getOptInAttribute = (tableName: string, attribute: string): string => {
            if (_.isNull(i_privileges) || defaultValues) return '0';
            let value: any = undefined;
            i_privileges.forEach((priv: IPrivileges) => {
                if (priv?.$?.id !== selPrivilegeId) return;
                priv?.Groups?.[0]?.Group?.forEach((group: any) => {
                    if (group?.$?.name === tableName) value = group?.$?.[attribute];
                });
            });
            return value === '1' ? '1' : '0';
        };
        const xmlDataString = `
          <Privilege>
              <Groups>
                <Group name="Global" visible="${getAttributeGroup('Global', 'visible')}">
                  <Tables global_settings="${getPrivilegesTable('Global', 'global_settings')}"/>
                </Group>
                <Group name="Screens" visible="${getAttributeGroup('Screens', 'visible')}">
                  <Tables boards="${getPrivilegesTable('Screens', 'boards')}" board_templates="${getPrivilegesTable('Screens', 'board_templates')}" board_template_viewers="${getPrivilegesTable('Screens', 'board_template_viewers')}"/>
                </Group>
                <Group name="Resources" visible="${getAttributeGroup('Resources', 'visible')}" resourceMode="${getAttributeGroup('Resources', 'resourceMode')}">
                  <Tables resources="${getPrivilegesTable('Resources', 'resources')}"/>
                </Group>
                <Group name="Editors" visible="${getAttributeGroup('Editors', 'visible')}">
                  <Tables player_data="${getPrivilegesTable('Editors', 'player_data')}"/>
                </Group>
                <Group name="EventParams"
                visible="${getAttributeGroup('EventParams', 'visible')}"
                data="${getAttributeGroup('EventParams', 'data')}"
                >
                  <Tables event_params="${getPrivilegesTable('EventParams', 'event_params')}"/>
                </Group>
                <Group name="Catalog" visible="${getAttributeGroup('Catalog', 'visible')}">
                  <Tables catalog_items="${getPrivilegesTable('Catalog', 'catalog_items')}" catalog_item_infos="${getPrivilegesTable('Catalog', 'catalog_item_infos')}" catalog_item_resources="${getPrivilegesTable('Catalog', 'catalog_item_resources')}" catalog_item_categories="${getPrivilegesTable('Catalog', 'catalog_item_categories')}" category_values="${getPrivilegesTable('Catalog', 'category_values')}"/>
                </Group>
                <Group name="Campaigns" visible="${getAttributeGroup('Campaigns', 'visible')}">
                  <Tables campaigns="${getPrivilegesTable('Campaigns', 'campaigns')}" campaign_events="${getPrivilegesTable('Campaigns', 'campaign_events')}" campaign_timelines="${getPrivilegesTable('Campaigns', 'campaign_timelines')}" campaign_timeline_sequences="${getPrivilegesTable('Campaigns', 'campaign_timeline_sequences')}" campaign_timeline_schedules="${getPrivilegesTable('Campaigns', 'campaign_timeline_schedules')}" campaign_sequences="${getPrivilegesTable('Campaigns', 'campaign_sequences')}" campaign_sequence_timelines="${getPrivilegesTable('Campaigns', 'campaign_sequence_timelines')}" campaign_sequence_schedules="${getPrivilegesTable('Campaigns', 'campaign_sequence_schedules')}" campaign_timeline_channels="${getPrivilegesTable('Campaigns', 'campaign_timeline_channels')}" campaign_timeline_chanels="${getPrivilegesTable('Campaigns', 'campaign_timeline_chanels')}" campaign_timeline_chanel_players="${getPrivilegesTable('Campaigns', 'campaign_timeline_chanel_players')}" campaign_timeline_board_viewer_channels="${getPrivilegesTable('Campaigns', 'campaign_timeline_board_viewer_channels')}" campaign_timeline_board_viewer_chanels="${getPrivilegesTable('Campaigns', 'campaign_timeline_board_viewer_chanels')}" campaign_timeline_board_templates="${getPrivilegesTable('Campaigns', 'campaign_timeline_board_templates')}" campaign_channels="${getPrivilegesTable('Campaigns', 'campaign_channels')}" campaign_channel_players="${getPrivilegesTable('Campaigns', 'campaign_channel_players')}" campaign_boards="${getPrivilegesTable('Campaigns', 'campaign_boards')}"/>
                </Group>
                <Group name="Transitions" visible="${getAttributeGroup('Transitions', 'visible')}">
                  <Tables transition_pools="${getPrivilegesTable('Transitions', 'transition_pools')}" transition_pool_items="${getPrivilegesTable('Transitions', 'transition_pool_items')}"/>
                </Group>
                <Group name="Scripts" visible="${getAttributeGroup('Scripts', 'visible')}">
                  <Tables scripts="${getPrivilegesTable('Scripts', 'scripts')}"/>
                </Group>
                <Group name="AdNet"
                    visible="${getAttributeGroup('AdNet', 'visible')}"
                    profile="${getAttributeGroup('AdNet', 'profile')}"
                    customerNetwork="${getAttributeGroup('AdNet', 'customerNetwork')}"
                    resellerNetwork="${getAttributeGroup('AdNet', 'resellerNetwork')}"
                    globalNetwork="${getAttributeGroup('AdNet', 'globalNetwork')}"
                    defaultAutoActivate="${getAttributeGroup('AdNet', 'defaultAutoActivate')}"
                    pairFeedback="${getAttributeGroup('AdNet', 'pairFeedback')}"
                    pairSetting="${getAttributeGroup('AdNet', 'pairSetting')}"
                    pairChat="${getAttributeGroup('AdNet', 'pairChat')}"
                    billing="${getAttributeGroup('AdNet', 'billing')}"
                    assets="${getAttributeGroup('AdNet', 'assets')}"
                    >
                  <Tables
                      adnet_rates="${getPrivilegesTable('AdNet', 'adnet_rates')}"
                      adnet_targets="${getPrivilegesTable('AdNet', 'adnet_targets')}"
                      adnet_packages="${getPrivilegesTable('AdNet', 'adnet_packages')}"
                      adnet_package_contents="${getPrivilegesTable('AdNet', 'adnet_package_contents')}"
                      adnet_package_targets="${getPrivilegesTable('AdNet', 'adnet_package_targets')}"
                  />
                </Group>
                <Group name="Music" visible="${getAttributeGroup('Music', 'visible')}">
                  <Tables
                  music_channels="${getPrivilegesTable('Music', 'music_channels')}"
                  music_channel_songs="${getPrivilegesTable('Music', 'music_channel_songs')}"/>
                </Group>
                <Group name="Stations"
                    visible="${getAttributeGroup('Stations', 'visible')}"
                    stationsNetwork="${getAttributeGroup('Stations', 'stationsNetwork')}"
                    updateOnSave="${getAttributeGroup('Stations', 'updateOnSave')}"
                    lanServer="${getAttributeGroup('Stations', 'lanServer')}"
                    zwave="${getAttributeGroup('Stations', 'zwave')}"
                  >
                  <Tables
                    branch_stations="${getPrivilegesTable('Stations', 'branch_stations')}"
                    station_ads="${getPrivilegesTable('Stations', 'station_ads')}"/>
                </Group>
                <Group name="Changelist" visible="${getAttributeGroup('Changelist', 'visible')}">
                  <Tables/>
                </Group>

                <Group name="SignStudio Dashboard"
                    visible="${getAttributeGroup('SignStudio Dashboard', 'visible')}"
                    navEnterprise="${getAttributeGroup('SignStudio Dashboard', 'navEnterprise')}"
                    navMain="${getAttributeGroup('SignStudio Dashboard', 'navMain')}"
                    navUser="${getAttributeGroup('SignStudio Dashboard', 'navUser')}"
                    navChat="${getAttributeGroup('SignStudio Dashboard', 'navChat')}"
                    navSearch="${getAttributeGroup('SignStudio Dashboard', 'navSearch')}"
                    navProducts="${getAttributeGroup('SignStudio Dashboard', 'navProducts')}"
                    resellerCollectUserEmail="${getAttributeGroup('SignStudio Dashboard', 'resellerCollectUserEmail')}"
                    showCustomHome="${getAttributeGroup('SignStudio Dashboard', 'showCustomHome')}"
                    >
                    <Tables/>
                </Group>
                <Group name="SignStudio Editor"
                    visible="${getAttributeGroup('SignStudio Editor', 'visible')}"
                    preview="${getAttributeGroup('SignStudio Editor', 'preview')}"
                    >
                  <Tables
                      player_data="${getPrivilegesTable('SignStudio Editor', 'player_data')}"
                  />
                </Group>

                <Group name="SignStudio Help"
                    visible="${getAttributeGroup('SignStudio Help', 'visible')}"
                    showVideos="${getAttributeGroup('SignStudio Help', 'showVideos')}"
                    showContact="${getAttributeGroup('SignStudio Help', 'showContact')}"
                    showChat="${getAttributeGroup('SignStudio Help', 'showChat')}"
                    customLinkChat="${getAttributeGroup('SignStudio Help', 'customLinkChat')}"
                    customLinkContact="${getAttributeGroup('SignStudio Help', 'customLinkContact')}"
                    >
                </Group>
                <Group name="SignStudio Downloads"
                    visible="${getAttributeGroup('SignStudio Downloads', 'visible')}"
                    customLink="${getAttributeGroup('SignStudio Downloads', 'customLink')}"
                    >
                </Group>

                <Group name="SignStudio Resources"
                    import="${getAttributeGroup('SignStudio Resources', 'import')}"
                    visible="${getAttributeGroup('SignStudio Resources', 'visible')}"
                    >
                  <Tables resources="${getPrivilegesTable('SignStudio Resources', 'resources')}"/>
                </Group>
                <Group name="SignStudio Players"
                    visible="${getAttributeGroup('SignStudio Players', 'visible')}"
                    filterByScene="${getOptInAttribute('SignStudio Players', 'filterByScene')}"
                    lockSceneAssign="${getOptInAttribute('SignStudio Players', 'lockSceneAssign')}"
                    >
                  <Tables
                    players="${getPrivilegesTable('SignStudio Players', 'players')}"
                  />
                </Group>
                <Group name="SignStudio Painter"
                    visible="${getAttributeGroup('SignStudio Painter', 'visible')}"
                    >
                  <Tables resources="${getPrivilegesTable('SignStudio Painter', 'resources')}"/>
                </Group>
                <Group name="SignStudio Scheduler" visible="${getAttributeGroup('SignStudio Scheduler', 'visible')}">
                  <Tables scheduler="${getPrivilegesTable('SignStudio Scheduler', 'scheduler')}"/>
                </Group>
                <Group name="SignStudio FasterQ" visible="${getAttributeGroup('SignStudio FasterQ', 'visible')}">
                  <Tables fasterQ="${getPrivilegesTable('SignStudio FasterQ', 'fasterQ')}"/>
                </Group>
                <Group name="SignStudio Music" visible="${getAttributeGroup('SignStudio Music', 'visible')}">
                  <Tables music="${getPrivilegesTable('SignStudio Music', 'music')}"/>
                </Group>
                <Group name="SignStudio Settings"
                    visible="${getAttributeGroup('SignStudio Settings', 'visible')}"
                    navTargets="${getAttributeGroup('SignStudio Settings', 'navTargets')}"
                    navGlobal="${getAttributeGroup('SignStudio Settings', 'navGlobal')}"
                    navModule="${getAttributeGroup('SignStudio Settings', 'navModule')}"
                    navScene="${getAttributeGroup('SignStudio Settings', 'navScene')}"
                    >
                  <Tables settings="${getPrivilegesTable('SignStudio Settings', 'settings')}"/>
                </Group>
                <Group name="SignStudio Account" visible="${getAttributeGroup('SignStudio Account', 'visible')}">
                  <Tables account="${getPrivilegesTable('SignStudio Account', 'account')}"/>
                </Group>
                <Group name="SignStudio Catalog" visible="${getAttributeGroup('SignStudio Catalog', 'visible')}">
                  <Tables catalog="${getPrivilegesTable('SignStudio Catalog', 'catalog')}"/>
                </Group>
                <Group name="SignStudio Events" visible="${getAttributeGroup('SignStudio Events', 'visible')}">
                  <Tables events="${getPrivilegesTable('SignStudio Events', 'events')}"/>
                </Group>
                <Group name="SignStudio Transitions" visible="${getAttributeGroup('SignStudio Transitions', 'visible')}">
                  <Tables transitions="${getPrivilegesTable('SignStudio Transitions', 'transitions')}"/>
                </Group>
                <Group name="SignStudio Adnet" visible="${getAttributeGroup('SignStudio Adnet', 'visible')}">
                  <Tables adnet="${getPrivilegesTable('SignStudio Adnet', 'adnet')}"/>
                </Group>
                <Group name="SignStudio AI"
                    aiSceneBuilder="${getAttributeGroup('SignStudio AI', 'aiSceneBuilder')}"
                    aiMediaProducer="${getAttributeGroup('SignStudio AI', 'aiMediaProducer')}"
                    aiAnimator="${getAttributeGroup('SignStudio AI', 'aiAnimator')}"
                    aiVideoBuilder="${getAttributeGroup('SignStudio AI', 'aiVideoBuilder')}"
                    >
                </Group>
              </Groups>
        </Privilege>
        `;

        if (_.isNull(i_privileges)) {
            /** mode (1): generate object from XML (we don't care about values as this is just a template) **/
            xml2js.parseString(xmlDataString, (err, xmlDataObject) => {
                if (err) return console.log('problem parsing privileges XML');
                callBack(err, xmlDataObject);
            });
            // parseString(xmlData, {attrkey: '_attr'}, function(err, result) {
            //     callBack(err, result);
            // });
        } else {
            /** mode (2): generate raw XML with real user data from AppStore so we can serialize and save to server **/
            callBack(null, xmlDataString);
        }
    }

    /**
     * Account is upgradable to enterprise if not already active, non active dashboard user or if not under a reseller
     * @return boolean;
     */
    static IsAccountUpgradable(i_user: IUser): boolean {
        // if account is plus it is still upgradable to new enterprise
        if (Lib.GetAccountVersion(i_user) === 'PLUS') {
            return true;
        }
        // if free user under ms then upgradable
        if (!i_user.enterprise && !i_user.dashboard && compareIds(i_user.resellerId, 1)) {
            return true;
        }
        // if already active, not upgradable
        if (Lib.IsResellerStatusActive(i_user)) {
            return false;
        }
        // if dashboard and obviously not active (from above) we are upgradable
        if (i_user.dashboard) {
            return true;
        }
        // Customer under a non-paying reseller whom the server confirms may self-upgrade (reseller inactive
        // AND lapsed > 6 months) — shows the same upgrade button a free account gets.
        if (i_user.canSelfUpgrade === true) {
            return true;
        }
        // if not under reseller we are upgradable
        return !i_user.enterprise;
    }

    // Alon added for wizard check ???
    static IsAccountUpgradable2(i_user: IUser): boolean {
        // if account is plus it is still upgradable to new enterprise
        if (Lib.GetAccountVersion(i_user) === 'PLUS') {
            return true;
        }
        // if free user under ms then upgradable
        if (!i_user.enterprise && !i_user.dashboard && compareIds(i_user.resellerId, 1)) {
            return true;
        }

        // if already active, not upgradable
        //??? if (Lib.IsResellerStatusActive(i_user)) {
        //???     return false;
        //??? }

        // if dashboard and obviously not active (from above) we are upgradable
        //???if (i_user.dashboard) {
        //???    return true;
        //???}
        // Customer under a non-paying reseller whom the server confirms may self-upgrade (reseller inactive
        // AND lapsed > 6 months) — shows the same upgrade button a free account gets.
        if (i_user.canSelfUpgrade === true) {
            return true;
        }
        // if not under reseller we are upgradable
        return !i_user.enterprise;
    }


    /**
     * IsBlockAddable
     * Is the block addable to a scene, depends on block constants and user has package access to it
     */
    @Memoizee({})
    static IsBlockAddable(i_blockAdding: IBlockLabelProp, i_user: IUser): boolean {
        if (i_blockAdding.free) return true;
        if (i_blockAdding.plus && Lib.GetAccountVersion(i_user) === 'PLUS') return true;
        if (i_blockAdding.enterprise && (Lib.GetAccountVersion(i_user) === 'ENTERPRISE_08' || Lib.GetAccountVersion(i_user) === 'ENTERPRISE_24')) return true;
        return false;
    }

    /**
     * IsEventAddBlocked
     * "free-limited" components (eventsPremium) are addable on Free, but adding an event to them
     * (receive event / event rule / init event) requires an upgrade. Returns true when the add
     * should be blocked and the upgrade dialog shown.
     */
    static IsEventAddBlocked(i_block: IBlockLabelProp, i_user: IUser): boolean {
        return !!i_block?.eventsPremium && Lib.IsFreeAccount(i_user);
    }

    /**
     * GetAccountStatus
     */
    static GetAccountStatus(i_serverReply: IAccountReplyV2): number {
        /** account status **/
        if (i_serverReply.businessUserInfo?.businessInfo?.accountStatus) {
            return Number(i_serverReply.businessUserInfo?.businessInfo?.accountStatus);
        } else if (i_serverReply.businessUserInfo?.resellerInfo?.accountStatus) {
            return Number(i_serverReply.businessUserInfo.resellerInfo.accountStatus);
        } else {
            return -2;
        }
    }

    /**
     *
     * takes everything into consideration to know if account is paying & active
     */
    static IsAccountSubscribingAndActive(i_user: IUser): boolean {
        if (Lib.GetAccountVersion(i_user) == 'DASH' && Lib.IsUserStatusActive(i_user))
            return true;
        if (Lib.GetAccountVersion(i_user) == 'PLUS' && Lib.IsResellerStatusActive(i_user))
            return true;
        if (Lib.GetAccountVersion(i_user) == 'ENTERPRISE_08' && Lib.IsResellerStatusActive(i_user))
            return true;
        if (Lib.GetAccountVersion(i_user) == 'ENTERPRISE_24' && Lib.IsResellerStatusActive(i_user))
            return true;
        return false;
    }

    static IsEnterpriseAndActive(i_user: IUser): boolean {
        const ver = Lib.GetAccountVersion(i_user);
        if ((ver === 'ENTERPRISE_08' || ver === 'ENTERPRISE_24') && Lib.IsResellerStatusActive(i_user))
            return true;
        return false;
    }

    /**
     *
     * is account subscribing mode (don't worry about active status)
     */
    static IsAccountSubscribing(user: IUser, pretty = false): boolean {
        return Lib.GetAccountVersion(user) === 'PLUS' || Lib.GetAccountVersion(user) === 'ENTERPRISE_08' || Lib.GetAccountVersion(user) === 'ENTERPRISE_24';
    }

    /**
     * Account is active if account status is 4
     * i_accountStatus can be
     *      i_serverReply.businessUserInfo?.businessInfo?.accountStatus
     *      i_serverReply.businessUserInfo.resellerInfo.accountStatus;
     * @return EnumAccountStatus
     */
    static IsResellerStatusActive(i_user: IUser): boolean {
        return compareIds(i_user.accountStatusReseller, 4);
    }

    static IsUserStatusActive(i_user: IUser): boolean {
        return compareIds(i_user.accountStatus, 4);
    }

    /**
     * isFreeAccount
     * @return boolean
     */
    static IsFreeAccount(user: IUser): boolean {
        return Lib.GetAccountVersion(user) === 'FREE';
    }

    /**
     * isAccessBlocked
     * return blocked if
     *      1. is not a free account (dash | enterprise), remember MediaSignage reseller_id 1 is account_status 4 and active
     *      2. its reseller is not active
     *      3. the account itself is not active
     * @return boolean
     */
    static IsAccessBlocked(user: IUser): boolean {
        /** if free account we never block  **/
        if (Lib.IsFreeAccount(user)) {
            return false;
        }
        /** enterprise or plus (same same), so only allow if reseller is active **/
        if (Lib.IsResellerStatusActive(user)) {
            return false;
        }
        /** allow account itself is an active account **/
        if (Lib.IsUserStatusActive(user)) {
            return false;
        }
        return true;
    }

    /**
     * @GetAccountVersionSubAccount
     1. Original pricing until 2024 :: pricingMethod = 1
     2. New pricing plus+ using quiet enterprise parent account : pricingMethod = 2
     3. New enterprise levels : pricingMethod = 3
     this is used by GetAccountVersion function as well as by Dynawebs to check if account is plus version
     **/
    static GetAccountVersionSubAccount(i_pricingMethod: number): AccountVersion {
        // old enterprise 2008 49, 99, 299 pricing structure
        if (compareIds(i_pricingMethod, EnumPricingMethod.ENTERPRISE_08)) {
            return 'ENTERPRISE_08';
        }
        // new $3.00 flat pricing structure per license
        if (compareIds(i_pricingMethod, EnumPricingMethod.PLUS)) {
            return 'PLUS';
        }
        // new enterprise 2024 pricing structure per license
        if (compareIds(i_pricingMethod, EnumPricingMethod.ENTERPRISE_24)) {
            return 'ENTERPRISE_24';
        }
    }

    static GetIsEnterprise(user: IUser): boolean {
        return user.enterprise === true;
    }

    /**
     * @GetAccountVersion
     1. Original pricing until 2024 : pricingMethod = 1
     2. New pricing plus+ using quiet enterprise parent account : pricingMethod = 2
     3. New enterprise levels : pricingMethod = 3
     **/
    static GetAccountVersion(user: IUser): AccountVersion {
        if (!user) {
            return 'UNKNOWN';
        }
        // dashboard
        if (user.dashboard) {
            return 'DASH';
        }
        // sun-account under reseller
        if (!compareIds(user.resellerId, '1')) {
            return Lib.GetAccountVersionSubAccount(user.pricingMethod);
        }
        // not under a reseller and not dashboard so must be free
        if (compareIds(user.resellerId, 1)) {
            return 'FREE';
        }
        // not sure what's going on
        return 'UNKNOWN';
    }

    static DevMode(): boolean {
        return inDevModeGulpValue;
    }

    static DevShow(msg): void {
        if (!inDevModeGulpValue) return;
        console.log(`${_.uniqueId()}|${moment().format('MM-DD-YY|hh:mm:ss')}> ${msg}`);
    }

    // static WorkOffline(): boolean {
    //   if (environment.workOffline)
    //     return true;
    //   return false;
    // }

    static mod(n, m) {
        return ((n % m) + m) % m;
    }

    static RandomString() {
        return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    static ComputeMask(accessMask): number {
        const bits = [1, 2, 4, 8, 16, 32, 64, 128];
        let computedAccessMask = 0;
        accessMask.forEach(value => {
            const bit = bits.shift();
            if (value) computedAccessMask = computedAccessMask + bit;

        });
        return computedAccessMask;
    }

    static GetAccessMask(accessMask): Array<any> {
        const checks = [];
        const bits = [1, 2, 4, 8, 16, 32, 64, 128];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < bits.length; i++) {
            // tslint:disable-next-line:no-bitwise
            const checked = (bits[i] & accessMask) > 0 ? true : false;
            checks.push(checked);
        }
        return checks;
    }

    static GetAccessMask3bit(accessMask): Array<any> {
        const checks = [];
        const bits = [1, 2, 4];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < bits.length; i++) {
            // tslint:disable-next-line:no-bitwise
            const checked = (bits[i] & accessMask) > 0 ? true : false;
            checks.push(checked);
        }
        return checks;
    }

    static GetADaysMaskList(accessMask): List<any> {
        let checks = List();
        const bits = [1, 2, 4, 8, 16, 32, 64];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < bits.length; i++) {
            // tslint:disable-next-line:no-bitwise
            const checked = (bits[i] & accessMask) > 0 ? true : false;
            checks = checks.push(checked);
        }
        return checks;
    }

    static GetADaysMask(accessMask): Array<any> {
        const checks = [];
        const bits = [1, 2, 4, 8, 16, 32, 64];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < bits.length; i++) {
            // tslint:disable-next-line:no-bitwise
            const checked = (bits[i] & accessMask) > 0 ? true : false;
            checks.push(checked);
        }
        return checks;
    }

    static GetWeekDaysMask(accessMask, toString = true): Array<any> {
        const checks = [];
        const bits = [1, 2, 4, 8, 16, 32, 64];
        bits.forEach((bit) => {
            // tslint:disable-next-line
            if ((bit & accessMask) > 0) {
                checks.push(toString ? bit.toString() : bit);
            }
        });
        return checks;
    }

    static IsNumber(value) {
        let res = true;
        res = _.isNaN(Number(value)) ? false : true;
        return res;
    }

    /**
     Pad zeros
     @method padZeros
     @param {Number} n value
     @param {Number} width pre-pad width
     @param {Number} z negative as in '-'
     @return {Number} zero padded string
     **/
    static PadZeros(n, width, z) {
        z = z || '0';
        n = n + '';
        return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
    }

    static DateFromAbsolute(value: number) {
        const year = Math.floor(value / 12);
        const month = value % 12 + 1;
        return {
            year,
            month
        };
    }

    static conlog(msg) {
        console.log(new Date().toTimeString().replace(/.*(\d{2}:\d{2}:\d{2}).*/, "$1") + ': ' + msg);
    }

    static openLiveChat(i_popup: boolean, i_user: IUser, i_appStatic: IAppStatic, router: Router = null) {
        /* version 1 chat */
        // popupWindow({url: i_appStatic.liveChatUrl, title: '_blank', w: 500, h: 500});

        /* version 2 chat */
        if (i_popup) {
            /** for user under resellers that have custom live chat setup that is not setup to the default old live_chat.html, open ilt **/
            if (i_user?.customStudio?.Studio?.Chat?.[0]?.$?.show == '1'
                && i_user?.customStudio?.Studio?.Chat?.[0]?.$?.link?.length > 5
                && i_user?.customStudio?.Studio?.Chat?.[0]?.$?.link?.indexOf('live_chat.html') === -1
            ) {
                console.log('Popup chat v2 ' + i_user?.customStudio?.Studio?.Chat?.[0]?.$?.link);
                return popupWindow({url: i_user?.customStudio.Studio.Chat[0].$.link, title: '_blank', w: 400, h: 450});
            } else {
                console.log('Popup chat v2 ' + i_appStatic.liveChatUrlV2);
                return popupWindow({url: `${i_appStatic.liveChatUrlV2}`, title: '_blank', w: 400, h: 450});
            }
        } else {
            if (i_user?.customStudio?.Studio?.Chat?.[0]?.$?.show == '1'
                && i_user?.customStudio?.Studio?.Chat?.[0]?.$?.link?.length > 5
                && i_user?.customStudio?.Studio?.Chat?.[0]?.$?.link?.indexOf('chatreseller') > -1
            ) {
                const v = i_user?.customStudio.Studio.Chat[0].$.link.split('=');
                if (v.length > 0) {
                    return router.navigateByUrl(`/help?chatreseller=${v[1]}`);

                } else {
                    return router.navigateByUrl('/help?chat=1');
                }
            } else {
                return router.navigateByUrl('/help?chat=1');
            }
        }
    }

    static getMeta(url): Promise<HTMLElement> {
        // const img = new Image();
        // img.onload = () => cb(null, img);
        // img.onerror = (err) => cb(err);
        // img.src = url;
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = (err) => reject(err);
            img.src = url;
        });
    }

    static EyeDropperConvert(sRGBHex) {
        const re = /rgb\(([0-9]+),\ ([0-9]+),\ ([0-9]+)\)/;
        const s: any = re.exec(sRGBHex);
        // @ts-ignore: Unreachable code error
        if (s === null) {
            return sRGBHex;
        } else {
            const rgb = (s[1] << 16) | (s[2] << 8) | (s[3] << 0);
            return '#' + (0x1000000 + rgb).toString(16).slice(1);
        }
    }

    /*********************************************************/
    /*      disable tslint starting here */
    /*********************************************************/

    /* tslint:disable */

    /**
     Hex to decimal converter
     @method hexToDecimal
     @param {String} h
     @return {Number} decimal
     **/
    static HexToDecimal(h) {
        if (!h || isNullOrUndefOrEmpty(h)) return 0;
        function hexfix(str) {
            var v, w;
            v = parseInt(str, 16);	// in rrggbb
            if (str.length == 3) {
                // nybble colors - fix to hex colors
                //  0x00000rgb              -> 0x000r0g0b
                //  0x000r0g0b | 0x00r0g0b0 -> 0x00rrggbb
                w = ((v & 0xF00) << 8) | ((v & 0x0F0) << 4) | (v & 0x00F);
                v = w | (w << 4);
            }
            return v.toString(16).toUpperCase();
        }

        var h = h.replace(/#/gi, '');
        h = hexfix(h);
        return parseInt(h, 16);
    }

    /**
     Decimal to hex converter
     @method decimalToHex
     @param {Number} d
     @return {String} hex
     **/
    static DecimalToHex(d) {
        d = String(d);
        if (d.match(/^#/)) return d;
        let hex = Number(d).toString(16);
        hex = "000000".substr(0, 6 - hex.length) + hex;
        return '#' + hex;
    }

    static Base64() {
        var r = "=", t = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

        function e(r, e) {
            var n = t.indexOf(r.charAt(e));
            if (-1 === n) throw "Cannot decode base64";
            return n;
        }

        function n(r, t) {
            var e = r.charCodeAt(t);
            if (e > 255) throw "INVALID_CHARACTER_ERR: DOM Exception 5";
            return e;
        }

        return {
            decode: function(t) {
                if (!t) return t;
                if (t === '') return t;
                return atob(t);
                // var n, h, a = 0, c = t.length, o = [];
                // if (t = String(t), 0 === c) return t;
                // if (c % 4 != 0) throw"Cannot decode base64";
                // for (t.charAt(c - 1) === r && (a = 1, t.charAt(c - 2) === r && (a = 2), c -= 4), n = 0; n < c; n += 4) h = e(t, n) << 18 | e(t, n + 1) << 12 | e(t, n + 2) << 6 | e(t, n + 3), o.push(String.fromCharCode(h >> 16, h >> 8 & 255, 255 & h));
                // switch (a) {
                //     case 1:
                //         h = e(t, n) << 18 | e(t, n + 1) << 12 | e(t, n + 2) << 6, o.push(String.fromCharCode(h >> 16, h >> 8 & 255));
                //         break;
                //     case 2:
                //         h = e(t, n) << 18 | e(t, n + 1) << 12, o.push(String.fromCharCode(h >> 16));
                // }
                // return o.join("");
            }, encode: function(e) {
                if (!e) return e;
                if (e === '') return e;
                return btoa(e);
                // if (1 !== arguments.length) throw"SyntaxError: exactly one argument required";
                // var h, a, c = [], o = (e = String(e)).length - e.length % 3;
                // if (0 === e.length) return e;
                // for (h = 0; h < o; h += 3) a = n(e, h) << 16 | n(e, h + 1) << 8 | n(e, h + 2), c.push(t.charAt(a >> 18)), c.push(t.charAt(a >> 12 & 63)), c.push(t.charAt(a >> 6 & 63)), c.push(t.charAt(63 & a));
                // switch (e.length - o) {
                //     case 1:
                //         a = n(e, h) << 16, c.push(t.charAt(a >> 18) + t.charAt(a >> 12 & 63) + r + r);
                //         break;
                //     case 2:
                //         a = n(e, h) << 16 | n(e, h + 1) << 8, c.push(t.charAt(a >> 18) + t.charAt(a >> 12 & 63) + t.charAt(a >> 6 & 63) + r);
                // }
                // return c.join("");
            }, VERSION: "1.0"
        };
    }

    // static GetSamples(): Object {
    //   return {
    //     1019: 'Sushi Restaurant,pro',
    //     1029: 'food menu board,pro',
    //     1007: 'Home and Garden,pro',
    //     1009: 'Hotel Lobby,pro',
    //     1016: 'Coffee Shop,pro',
    //     1011: 'Hobby Shop,pro',
    //     1013: 'Sports Bar,pro',
    //     1014: 'Museum,pro',
    //     1017: 'Bank,pro',
    //     1018: 'Gas Station,pro',
    //     1020: 'Casino,pro',
    //     1000: 'Travel,pro',
    //     1021: 'Bicycle Shop,pro',
    //     1022: 'Tanning Salon,pro',
    //     1023: 'Pharmacy,pro',
    //     1024: 'Laser Away,pro',
    //     1025: 'Dentistry,pro',
    //     1026: 'Clothing store,pro',
    //     1027: 'Golf club,pro',
    //     1028: 'RC Heli,pro',
    //     1030: 'seven eleven,pro',
    //     1031: 'Subway,pro',
    //     1032: 'Super market,pro',
    //     1033: 'Investment Group,pro',
    //     1035: 'Synagogue,pro',
    //     1036: 'Dry Cleaning,pro',
    //     1037: 'Ice Cream Shop,pro',
    //     1038: 'Real Estate office,pro',
    //     1039: 'Night Club,pro',
    //     1040: 'Hockey,pro',
    //     1041: 'Train Station,pro',
    //     1042: 'Realtor,pro',
    //     1043: 'Toy Store,pro',
    //     1044: 'Indian Restaurant,pro',
    //     1045: 'Library,pro',
    //     1046: 'Movie Theater,pro',
    //     1047: 'Airport,pro',
    //     1048: 'LAX,pro',
    //     100310: 'Motel,pro',
    //     100301: 'Parks and Recreations,pro',
    //     100322: 'Corner Bakery,pro',
    //     100331: 'Retirement home,pro',
    //     100368: 'Navy recruiting office,pro',
    //     100397: 'Martial arts school,pro',
    //     100414: 'Supercuts,pro',
    //     100432: 'The UPS Store,pro',
    //     100438: 'Cruise One,pro',
    //     100483: 'Car service,pro',
    //     100503: 'fedex kinkos,pro',
    //     100510: 'veterinarian,pro',
    //     100556: 'YMCA,pro',
    //     100574: 'Tax services,pro',
    //     100589: 'Wedding planner,pro',
    //     100590: 'Cleaning services,pro',
    //     100620: 'Pet Training,pro',
    //     100661: 'Gymboree Kids,pro',
    //     100677: 'Trader Joes,pro',
    //     100695: 'Men Haircuts,pro',
    //     100722: 'Jiffy Lube,pro',
    //     100738: 'Toyota  car dealer,pro',
    //     100747: 'Winery,pro',
    //     100771: 'Savings and Loans,pro',
    //     100805: 'Nail Salon,pro',
    //     100822: 'Weight Watchers,pro',
    //     100899: 'Dollar Tree,pro',
    //     100938: 'Western Bagles,pro',
    //     100959: 'Kaiser Permanente,pro',
    //     300143: 'Funeral home,pro',
    //     205734: 'Church,pro',
    //     220354: 'College,pro',
    //     206782: 'Dr Waiting Room,pro',
    //     300769: 'NFL Stadium,pro',
    //     301814: 'University Campus,pro',
    //     303038: 'Day care,pro',
    //     304430: 'GameStop,pro',
    //     307713: 'Del Taco,pro',
    //     305333: 'General Hospital,pro',
    //     305206: 'Starbucks,pro',
    //     308283: 'training and fitness,pro',
    //     311519: 'High school hall,pro',
    //     309365: 'Winery,pro',
    //     310879: 'Law Firm,pro',
    //     1001: 'Health Club,pro',
    //     1002: 'Gym,pro',
    //     1003: 'Flower Shop,pro',
    //     1004: 'Car Dealership,pro',
    //     1012: 'Pet Shop,pro',
    //     1005: 'Hair Salon,pro',
    //     1209: 'Motorcycle shop,lite',
    //     1210: 'Sushi and Grill,lite',
    //     1211: 'the Coffee Shop,lite',
    //     1212: 'Pizzeria,lite',
    //     1213: 'Music Store,lite',
    //     1214: 'Diner,lite',
    //     1215: 'the Hair Salon,lite',
    //     1216: 'Dentist,lite',
    //     1203: 'Jewelry,lite',
    //     1217: 'Crossfit,lite',
    //     1218: 'Copy and Print shop,lite',
    //     1219: 'Antique Store,lite',
    //     1220: 'Clock Repair Store,lite',
    //     1221: 'Eastern Cuisine,lite',
    //     1222: 'the Toy Store,lite',
    //     1223: 'Pet Store Grooming,lite',
    //     1224: 'the Veterinarian,lite',
    //     1225: 'Tattoo Parlor,lite',
    //     1226: 'Camera Store,lite',
    //     1228: 'Bike shop,lite',
    //     1229: 'Gun Shop,lite',
    //     1230: 'Chiropractic Clinic,lite',
    //     1231: 'French Restaurant,lite',
    //     1233: 'Winery,lite',
    //     1232: 'Mexican Taqueria,lite',
    //     1234: 'Bistro Restaurant,lite',
    //     1235: 'Vitamin Shop,lite',
    //     1227: 'Tailor Shop,lite',
    //     1236: 'Computer Repair,lite',
    //     1237: 'Car Detail,lite',
    //     1238: 'Asian Restaurants,lite',
    //     1239: 'Marijuana Dispensary,lite',
    //     1240: 'the Church,lite',
    //     1241: 'Synagogue,lite',
    //     1242: 'Frozen Yogurt Store,lite',
    //     1244: 'Baby Day Care,lite',
    //     1052: 'Car wash,lite',
    //     1053: 'Smoke shop,lite',
    //     1054: 'Yoga place,lite',
    //     1055: 'Laundromat,lite',
    //     1056: 'Baby clothes,lite',
    //     1057: 'Travel agency,lite',
    //     1058: 'Real Estate agent,lite'
    //   }
    // }
}
